import {User} from "./user.model";
import {Supervisor} from "./supervisor.model";
import {LegalEntity} from "./legal-entity.model";
import {ILegalCase} from "./interface/legal-case.model.interface";
import {CustomerFactory} from "../factory/customer.factory";
import moment, {Moment} from "moment";
import {ILegalCaseState} from "./interface/legal-case-state.model.interface";
import {IDepartment} from "./interface/department.model.interface";
import {IArrangements} from "./interface/arrantements.model.interface";
import {LegalCaseLinkService} from "../../core/services/legal-case-link.service";
import {IOrderType} from "./interface/orderType.model.interface";
import {ILegalCaseContractSummary} from "./interface/legal-case-contract-summary.model.interface";

export abstract class LegalCase {
  private readonly _id: number;
  private readonly _duration: number;
  private readonly _briefcaseNumber: number;
  private readonly _oldBriefcaseNumber: string;
  private readonly _oldWayOfTermination: string;
  private readonly _oldStatus: string;
  private readonly _instructing: LegalEntity;
  private readonly _supervisor: Supervisor;
  private readonly _vindicator: User;
  private readonly _fieldAttorney?: User;
  private readonly _coordinator?: User;
  private readonly _additionalFieldAttorney?: User;
  private readonly _gipAdministrators: User[];
  private readonly _state: ILegalCaseState;
  private readonly _department: IDepartment;
  private readonly _description: string;
  private readonly _createdAt: Moment;
  private readonly _coreCreatedAt: Moment;
  private readonly _endAt: Moment;
  private readonly _settledAt: Moment;
  private readonly _settled: boolean;
  private readonly _arrangements: IArrangements;
  private readonly _coreId: number;
  private readonly _orderType: IOrderType;
  private readonly _contractSummary: ILegalCaseContractSummary;
  private readonly _criminalLawyer: User;
  private readonly _phoneOperator: User;

  protected constructor(instance: ILegalCase) {
    this._gipAdministrators = [];
    this._id = instance.id;
    this._coreId = instance.coreId;
    this._duration = instance.duration;
    this._briefcaseNumber = instance.briefcaseNumber;
    this._oldBriefcaseNumber = instance.oldBriefcaseNumber;
    this._instructing = CustomerFactory.createLegalEntity(instance.instructing);
    this._supervisor = new Supervisor(instance.supervisor);
    this._vindicator = new User(instance.vindicator);
    if (instance.fieldAttorney) {
      this._fieldAttorney = new User(instance.fieldAttorney);
    }
    if (instance.coordinator) {
      this._coordinator = new User(instance.coordinator);
    }
    if (instance.additionalFieldAttorney) {
      this._additionalFieldAttorney = new User(instance.additionalFieldAttorney);
    }
    if (instance.criminalLawyer) {
      this._criminalLawyer = new User(instance.criminalLawyer);
    }
    if (instance.gipAdministrators?.length) {
      this._gipAdministrators = instance.gipAdministrators.map(gipAdministrator => new User(gipAdministrator));
    }
    this._description = instance.description;
    this._state = instance.state;
    this._settled = instance.settled;
    this._department = instance.department;
    if (instance.endAt) {
      this._endAt = moment(instance.endAt);
    }
    if (instance.settledAt) {
      this._settledAt = moment(instance.settledAt);
    }
    if (instance.createdAt) {
      this._createdAt = moment(instance.createdAt);
    }
    if (instance.coreCreatedAt) {
      this._coreCreatedAt = moment(instance.coreCreatedAt);
    }
    this._arrangements = instance.arrangements;
    if (instance.orderType) {
      this._orderType = instance.orderType
    }
    if (instance.contractSummary) {
      this._contractSummary = instance.contractSummary;
    }

    this._oldBriefcaseNumber = instance.oldBriefcaseNumber;
    this._oldWayOfTermination = instance.oldWayOfTermination;
    this._oldStatus = instance.oldStatus;

    if (instance.phoneOperator) {
      this._phoneOperator = new User(instance.phoneOperator);
    }
  }

  get id(): number {
    return this._id;
  }

  get duration(): number {
    return this._duration;
  }

  get briefcaseNumber(): number {
    return this._briefcaseNumber;
  }

  get instructing(): LegalEntity {
    return this._instructing;
  }

  get supervisor(): Supervisor {
    return this._supervisor;
  }

  get vindicator(): User {
    return this._vindicator;
  }

  get fieldAttorney(): User {
    return this._fieldAttorney;
  }

  get coordinator(): User {
    return this._coordinator;
  }

  get additionalFieldAttorney(): User {
    return this._additionalFieldAttorney;
  }

  get gipAdministrators(): User[] {
    return this._gipAdministrators;
  }

  get criminalLawyer(): User {
    return this._criminalLawyer;
  }

  get state(): ILegalCaseState {
    return this._state;
  }

  get department(): IDepartment {
    return this._department;
  }

  get description(): string {
    return this._description;
  }

  get createdAt(): moment.Moment {
    return this._createdAt;
  }

  get coreCreatedAt(): moment.Moment {
    return this._coreCreatedAt;
  }

  get endAt(): moment.Moment {
    return this._endAt;
  }

  get settledAt(): moment.Moment {
    return this._settledAt;
  }

  get settled(): boolean {
    return this._settled;
  }

  get arrangements(): IArrangements {
    return this._arrangements;
  }

  get orderType(): IOrderType {
    return this._orderType;
  }

  isClosedOrReleased(): boolean {
    return this._state.ending;
  }

  getFullCrew(): User[] {
    return [this._vindicator, this._fieldAttorney, this._additionalFieldAttorney]
      .filter(instance => instance);
  }

  get coreId(): number {
    return this._coreId;
  }

  isEditable(): boolean {
    return !this._state.moved && !this._state.ending;
  }

  isInvoiceable() {
    return this.isEditable();
  }

  abstract isInspectionable();

  isReportable() {
    return this.isEditable();
  }

  isMoved() {
    return this._state.moved;
  }

  prepareUrl() {
    return LegalCaseLinkService.createUrlForLegalCaseStatic(this._department.type, this._id)
  }

  get oldBriefCaseNumber(): string {
    return this._oldBriefcaseNumber;
  }

  get oldWayOfTermination(): string {
    return this._oldWayOfTermination;
  }

  get oldStatus(): string {
    return this._oldStatus;
  }

  get contractSummary(): ILegalCaseContractSummary {
    return this._contractSummary;
  }

  get phoneOperator(): User {
    return this._phoneOperator;
  }

  abstract isIncasoable();

  abstract getCustomers(): LegalEntity[];
}
